import React from 'react';
import Icon from '../../../components/AppIcon';

const RetentionInsights = ({ stats }) => {
  const getInsights = () => {
    const insights = [];

    // Retention rate insights
    if (stats?.retentionRate >= 85) {
      insights?.push({
        type: 'success',
        icon: 'TrendingUp',
        title: 'Excellent Retention',
        message: 'Your retention rate is above industry average. Keep up the great work!',
        action: 'Continue current engagement strategies'
      });
    } else if (stats?.retentionRate >= 70) {
      insights?.push({
        type: 'info',
        icon: 'Info',
        title: 'Good Retention',
        message: 'Retention is solid but has room for improvement.',
        action: 'Focus on member engagement programs'
      });
    } else {
      insights?.push({
        type: 'warning',
        icon: 'AlertTriangle',
        title: 'Retention Needs Attention',
        message: 'Your retention rate is below optimal levels.',
        action: 'Implement retention improvement strategies'
      });
    }

    // At-risk members insights
    if (stats?.atRiskMembers > 0) {
      const riskPercentage = ((stats?.atRiskMembers / stats?.totalMembers) * 100)?.toFixed(1);
      insights?.push({
        type: riskPercentage > 15 ? 'warning' : 'info',
        icon: 'AlertCircle',
        title: 'At-Risk Members Detected',
        message: `${stats?.atRiskMembers} members (${riskPercentage}%) are at high churn risk.`,
        action: 'Review churn risk list and take action'
      });
    }

    // Engagement insights
    if (stats?.avgEngagementScore < 50) {
      insights?.push({
        type: 'warning',
        icon: 'Activity',
        title: 'Low Engagement',
        message: 'Average engagement score is below target.',
        action: 'Launch engagement campaigns and challenges'
      });
    } else if (stats?.avgEngagementScore >= 70) {
      insights?.push({
        type: 'success',
        icon: 'Activity',
        title: 'Strong Engagement',
        message: 'Members are highly engaged with your gym.',
        action: 'Maintain current engagement initiatives'
      });
    }

    // Churn rate insights
    if (stats?.churnRate > 15) {
      insights?.push({
        type: 'warning',
        icon: 'TrendingDown',
        title: 'High Churn Rate',
        message: 'Churn rate exceeds acceptable threshold.',
        action: 'Investigate root causes and implement retention programs'
      });
    }

    return insights;
  };

  const insights = getInsights();

  const getTypeStyles = (type) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-success/10',
          border: 'border-success',
          icon: 'text-success',
          text: 'text-success'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10',
          border: 'border-warning',
          icon: 'text-warning',
          text: 'text-warning'
        };
      case 'info':
        return {
          bg: 'bg-primary/10',
          border: 'border-primary',
          icon: 'text-primary',
          text: 'text-primary'
        };
      default:
        return {
          bg: 'bg-muted',
          border: 'border-border',
          icon: 'text-muted-foreground',
          text: 'text-foreground'
        };
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon name="Lightbulb" size={20} className="text-primary" />
        <h2 className="text-lg font-semibold text-foreground">Retention Insights</h2>
      </div>

      <div className="space-y-4">
        {insights?.length === 0 ? (
          <div className="text-center py-6">
            <Icon name="Info" size={32} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No insights available yet</p>
          </div>
        ) : (
          insights?.map((insight, index) => {
            const styles = getTypeStyles(insight?.type);
            return (
              <div
                key={index}
                className={`${styles?.bg} border ${styles?.border} rounded-lg p-4`}
              >
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 ${styles?.icon}`}>
                    <Icon name={insight?.icon} size={20} />
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${styles?.text} mb-1`}>
                      {insight?.title}
                    </h3>
                    <p className="text-sm text-foreground mb-2">
                      {insight?.message}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Icon name="ArrowRight" size={12} />
                      <span>{insight?.action}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Quick Stats */}
      <div className="mt-6 pt-4 border-t border-border">
        <div className="text-sm font-semibold text-foreground mb-3">Key Metrics</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-background rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Active Rate</div>
            <div className="text-lg font-bold text-foreground">
              {stats?.totalMembers > 0
                ? ((stats?.activeMembers / stats?.totalMembers) * 100)?.toFixed(1)
                : 0}%
            </div>
          </div>
          <div className="bg-background rounded-lg p-3">
            <div className="text-xs text-muted-foreground mb-1">Avg Engagement</div>
            <div className="text-lg font-bold text-foreground">
              {stats?.avgEngagementScore}/100
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RetentionInsights;